import React, { useEffect, memo, useCallback, useState, useMemo } from 'react';
import { Header, Message } from 'semantic-ui-react';
import { useDispatch } from 'react-redux';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { useBookOperationsZoneBased } from '../../hooks/entities/useBookOperationsZoneBased';
import { fetchAllBooks } from '../../slices/bookWorksheetDataSlice';
import { DeleteBookModal } from '../modals';
import LitGroupBookListItem from './LitGroupBookListItem';

/**
 * NestedLitGroupsBooksListEnh component
 * Enhanced nested list showing Literary Groups with their Books
 * Combines functionality from LiteraryGroupBookList with bookWorksheet styling
 * @param {Object} props - Component props
 * @param {Function} props.onBookSelect - Callback when a book is selected
 * @param {Function} props.onNewBook - Callback when "New Book" is clicked
 * @returns {JSX.Element} NestedLitGroupsBooksListEnh component
 */
const NestedLitGroupsBooksListEnh = memo(({ onBookSelect, onNewBook }) => {
  const dispatch = useDispatch();
  const sharedData = useSharedData();
  const { deleteBook, getBookDeleteImpact } = useBookOperationsZoneBased();

  // Local state for expand/collapse and selection
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedLitGroup, setSelectedLitGroup] = useState(null);
  const [deletingBookId, setDeletingBookId] = useState(null);
  const [allBooksLoaded, setAllBooksLoaded] = useState(false);

  // Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);

  // Extract data from shared hook
  const {
    litGroups,
    books,
    selections,
    loading,
    errors
  } = sharedData;

  // Map to arrays for safety (memoized to prevent unnecessary re-renders)
  const litGroupsArray = useMemo(() =>
    Array.isArray(litGroups) ? litGroups : [],
    [litGroups]
  );
  const booksArray = useMemo(() =>
    Array.isArray(books) ? books : [],
    [books]
  );
  const bookId = selections?.bookId;
  const litGroupId = selections?.litGroupId;
  const litGroupsLoading = loading?.litGroups ? 'loading' : 'idle';
  const litGroupsError = errors?.litGroups;

  // Get functions from shared data
  const fetchLitGroups = sharedData.actions.fetchLitGroups;
  const fetchBooks = sharedData.actions.fetchBooks;
  const setLitGroupSelection = sharedData.actions.setLitGroupSelection;
  const setBookSelection = sharedData.actions.setBookSelection;

  // Fetch literary groups when component mounts
  useEffect(() => {
    fetchLitGroups();
  }, [fetchLitGroups]);

  // Pre-load all books after literary groups are loaded
  useEffect(() => {
    if (litGroupsArray.length > 0 && !allBooksLoaded) {
      console.log('[PRELOAD] Starting bulk book fetch for', litGroupsArray.length, 'literary groups');

      // Use Redux thunk to fetch all books at once and store them in state
      dispatch(fetchAllBooks())
        .unwrap()
        .then((result) => {
          console.log('[PRELOAD] Bulk fetch successful, books stored in Redux');
          setAllBooksLoaded(true);
        })
        .catch(error => {
          console.error('[PRELOAD] Bulk fetch failed, using fallback individual fetches:', error);

          // Fallback: fetch books for each literary group individually
          const fetchPromises = litGroupsArray.map(group =>
            fetchBooks(group.uuid).catch(error => {
              console.warn(`[PRELOAD] Failed to fetch books for group ${group.name}:`, error);
              return null; // Don't fail the entire operation
            })
          );

          return Promise.all(fetchPromises).then(() => {
            console.log('[PRELOAD] Individual fetches completed');
            setAllBooksLoaded(true);
          });
        });
    }
  }, [litGroupsArray, allBooksLoaded, dispatch, fetchBooks]);

  // Update selectedLitGroup when litGroupId changes
  useEffect(() => {
    if (litGroupId) {
      const group = litGroupsArray.find(lg => lg.uuid === litGroupId);
      setSelectedLitGroup(group || null);

      // Auto-expand the selected group
      if (group) {
        setExpandedGroups(prev => ({
          ...prev,
          [group.uuid]: true
        }));
      }
    } else {
      setSelectedLitGroup(null);
    }
  }, [litGroupId, litGroupsArray]);

  // Toggle expand/collapse for a literary group
  const toggleExpand = useCallback((groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  }, []);

  // Memoized books-by-group map for performance
  const booksByGroup = useMemo(() => {
    const groupMap = {};
    booksArray.forEach(book => {
      const groupId = book.litGroupId || book.literaryGroupId;
      if (groupId) {
        if (!groupMap[groupId]) {
          groupMap[groupId] = [];
        }
        groupMap[groupId].push(book);
      }
    });
    return groupMap;
  }, [booksArray]);

  // Handle literary group selection (simplified - books are pre-loaded)
  const handleLitGroupSelect = useCallback((group) => {
    // Clear book selection FIRST, before any other operations
    setBookSelection(null);

    setSelectedLitGroup(group);
    setLitGroupSelection(group.uuid);

    // Books are pre-loaded, just log for debugging
    const existingBooks = booksByGroup[group.uuid] || [];
    console.log('[PRELOAD] Using pre-loaded books for group:', group.name, '- count:', existingBooks.length);

    // Auto-expand the selected group
    setExpandedGroups(prev => ({
      ...prev,
      [group.uuid]: true
    }));
  }, [setLitGroupSelection, setBookSelection, booksByGroup]);

  // Handle book selection
  const handleBookSelect = useCallback((book, group) => {
    setSelectedLitGroup(group);
    setBookSelection(book.uuid);

    if (onBookSelect) {
      onBookSelect(book);
    }
  }, [setBookSelection, onBookSelect]);

  // Handle new book
  const handleNewBook = useCallback((group) => {
    if (onNewBook) {
      onNewBook(group);
    }
  }, [onNewBook]);

  // Handle book deletion - open modal
  const handleDeleteBook = useCallback((book) => {
    setBookToDelete(book);
    setDeleteModalOpen(true);
  }, []);

  // Handle modal confirm delete
  const handleConfirmDelete = useCallback(async (deleteType) => {
    if (!bookToDelete) return;

    const bookName = bookToDelete.title || bookToDelete.name || 'Untitled Book';

    try {
      setDeletingBookId(bookToDelete.uuid);
      console.log('[DELETE] Starting book deletion:', bookName);

      await deleteBook({
        bookId: bookToDelete.uuid,
        deleteType: deleteType,
        options: {
          cascade: 'cascade',
          cleanup: 'cleanup'
        }
      });

      console.log('[DELETE] Book deleted successfully:', bookName);

      // Close modal and reset state
      setDeleteModalOpen(false);
      setBookToDelete(null);
    } catch (error) {
      console.error('[DELETE] Failed to delete book:', error);
      alert(`Failed to delete book "${bookName}". Please try again.\n\nError: ${error.message}`);
    } finally {
      setDeletingBookId(null);
    }
  }, [bookToDelete, deleteBook]);

  // Handle modal cancel
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setBookToDelete(null);
  }, []);


  return (
    <div className="bw-nested-litgroups-books-container">
      <Header className="bw-hdr-with-prompt" as="h3">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}>
          <span>Literary Groups & Books</span>
          {litGroupsLoading === 'loading' && (
            <div className="bw-book-message" style={{
              backgroundColor: '#F8FFFF',
              border: 'solid 1px #a9d5de',
              marginLeft: '10px'
            }}>
              <p>Loading groups...</p>
            </div>
          )}
        </div>
      </Header>

      {/* Literary Groups and Books list */}
      {litGroupsLoading === 'failed' ? (
        <Message negative>
          <Message.Header>Error Loading Literary Groups</Message.Header>
          <p>{litGroupsError || 'An unknown error occurred'}</p>
        </Message>
      ) : litGroupsArray && litGroupsArray.length > 0 ? (
        <div className="ui segment bw-nested-list">
          {console.log('litGroupsArray', litGroupsArray)}
          {litGroupsArray.map((group) => (
            <LitGroupBookListItem
              key={group.uuid}
              group={group}
              isSelected={selectedLitGroup && selectedLitGroup.uuid === group.uuid}
              isExpanded={expandedGroups[group.uuid] || false}
              selectedBookId={bookId}
              books={booksByGroup[group.uuid] || []}
              onGroupSelect={handleLitGroupSelect}
              onBookSelect={handleBookSelect}
              onNewBook={handleNewBook}
              onToggleExpand={toggleExpand}
              onDeleteBook={handleDeleteBook}
              deletingBookId={deletingBookId}
            />
          ))}
        </div>
      ) : (
        <div className="bw-empty-list-message">
          <p>No literary groups found. Create a new literary group to get started.</p>
        </div>
      )}

      {/* Delete Book Modal */}
      <DeleteBookModal
        open={deleteModalOpen}
        book={bookToDelete}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        getDeleteImpact={getBookDeleteImpact}
        loading={deletingBookId === bookToDelete?.uuid}
      />
    </div>
  );
});

export default NestedLitGroupsBooksListEnh;
